import React from "react";
import { Link } from "react-router-dom";
import { Users, User, Building2, GraduationCap, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

const OFFERTA = [
  { icon: User, title: "Supervisione individuale", text: "Per terapeutə che lavorano con persone che appartengono a gruppi minorizzati e in relazioni non convenzionali, e sentono il bisogno di una formazione clinica specifica e situata." },
  { icon: Users, title: "Supervisioni di gruppo", text: "Percorsi in piccoli gruppi, articolati per livelli, che alternano teoria, casi clinici e discussione collegiale." },
  { icon: Building2, title: "Supervisioni per organizzazioni", text: "Interventi su richiesta presso associazioni, servizi, enti del terzo settore e gruppi di lavoro professionali." },
];

const PER_CHI = [
  { text: "Psicologə, psicoterapeutə, counselor che si trovano a lavorare con persone non monogame ma non si sentono abbastanza preparatə." },
  { text: "Professionistə che vogliono approfondire sessualità, queer theory, relazioni non convenzionali in modo clinicamente fondato." },
  { text: "Personale medico che necessita di formazione specifica e linee guida per lavorare con gruppi minorizzati (es. approccio affermativo con persone transgender)." },
];

const ESPERIENZA = [
  { where: "Bussole LGBT+ · Pavia", detail: "Supervisione 2025 con focus specifico sulle non monogamie consensuali." },
  { where: "Asterisco · Milano", detail: "Supervisioni di gruppo — 2 edizioni completate del livello 1, livello 2 in programma settembre 2026." },
  { where: "Pratica clinica affermativa", detail: "6 anni di lavoro con clientela LGBTQ+ e non monogama, in setting individuale e di coppia/rete." },
];

export default function Supervisioni() {
  return (
    <div data-testid="page-supervisioni">
      {/* HERO */}
      <section className="relative overflow-hidden bg-arancio/30 border-b border-viola/10 dark:bg-viola/40 dark:border-cream/10">
        <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-fucsia/15 blur-3xl animate-blob" aria-hidden />
        <div className="container-editorial relative py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <span className="pill" data-testid="sup-badge">Formazione tra pari · Supervisione clinica</span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-black dark:text-cream">
              Uno spazio di <span className="italic font-normal">supervisione clinica</span> per chi lavora <span className="text-fucsia">sui margini della norma</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-viola/80 dark:text-cream/80 leading-relaxed">
              La formazione universitaria e post-lauream raramente prepara a lavorare con clientela non monogama, kink, queer, transgender o con configurazioni relazionali plurime. Questo spazio nasce per colmare quel vuoto — con rigore clinico, sguardo affermativo e responsabilità politica.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contatti" className="btn-primary" data-testid="sup-cta-primary">Richiedi videocall conoscitiva</Link>
              <a href="#come-funziona" className="btn-ghost">Come funziona ↓</a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="rounded-2xl bg-viola text-cream p-8 shadow-[0_30px_60px_-30px_rgba(44,20,69,0.4)]" data-testid="sup-hero-callout">
              <p className="text-xs uppercase tracking-widest text-giallo font-semibold">Sempre gratuito · Per tutte le modalità</p>
              <p className="mt-3 font-display text-4xl leading-none">Videocall<br />conoscitiva</p>
              <p className="mt-4 flex items-baseline gap-2 font-display">
                <span className="text-5xl">~20</span>
                <span className="text-lg text-cream/70">minuti</span>
              </p>
              <p className="mt-4 text-sm text-cream/80">Un primo incontro breve per definire insieme il focus specifico prima di iniziare qualsiasi percorso — individuale, di gruppo o per organizzazioni.</p>
            </div>
          </div>
        </div>
      </section>

      {/* COSA OFFRO */}
      <section className="container-editorial py-20">
        <div className="max-w-2xl">
          <span className="pill">01 · Cosa offro</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">Tre modalità di supervisione.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {OFFERTA.map((o, i) => (
            <div key={o.title} className={`card-flat ${i === 1 ? "bg-lilla" : i === 2 ? "bg-arancio/40" : ""}`} data-testid={`sup-offer-${i}`}>
              <o.icon className="text-fucsia" />
              <h3 className="mt-4 font-display text-2xl">{o.title}</h3>
              <p className="mt-3 text-sm text-viola/80 leading-relaxed">{o.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PER CHI */}
      <section className="bg-lilla/40 border-y border-viola/10">
        <div className="container-editorial py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="pill">02 · Per chi</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">A chi si rivolgono queste supervisioni?</h2>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {PER_CHI.map((p) => (
              <div key={p.text.slice(0, 40)} className="rounded-xl border border-viola/10 bg-white p-6 flex gap-4 items-start" data-testid={`sup-audience-${p.text.slice(0, 30).replace(/\s+/g, "-").toLowerCase()}`}>
                <CheckCircle2 className="text-fucsia mt-1 shrink-0" size={22} />
                <p className="text-viola/85 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ESPERIENZA */}
      <section className="bg-viola text-cream">
        <div className="container-editorial py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-giallo text-viola"><GraduationCap size={20} /></div>
            <span className="pill mt-6 bg-giallo text-viola">03 · Esperienza</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">Un percorso già in cammino.</h2>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {ESPERIENZA.map((e) => (
              <div key={e.where} className="rounded-xl border border-cream/15 p-6" data-testid={`sup-exp-${e.where.split(" ")[0].toLowerCase()}`}>
                <p className="text-xs uppercase tracking-widest text-giallo font-semibold">{e.where}</p>
                <p className="mt-2 text-cream/85 leading-relaxed">{e.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="container-editorial py-20">
        <div className="max-w-3xl">
          <span className="pill">04 · Come funziona</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight dark:text-cream">Una videocall prima di tutto.</h2>
          <p className="mt-6 text-viola/80 dark:text-cream/80 text-lg leading-relaxed">
            Ogni percorso — individuale, di gruppo o per organizzazioni — inizia con una <strong>videocall conoscitiva gratuita di circa 20 minuti</strong>. È un momento breve per capire insieme il focus specifico che ti serve, gli obiettivi e la cornice più adatta prima di iniziare.
          </p>
          <p className="mt-4 text-sm text-viola/60 dark:text-cream/60 leading-relaxed">
            La durata effettiva delle sessioni di supervisione varia in base alla modalità scelta.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-viola/10 bg-white dark:bg-viola/40 dark:border-cream/10 p-8" data-testid="sup-modality-ind">
            <p className="text-xs uppercase tracking-widest text-fucsia font-semibold">Individuale</p>
            <h3 className="mt-2 font-display text-3xl dark:text-cream">Supervisione one-to-one</h3>
            <dl className="mt-6 space-y-3 text-viola/85 dark:text-cream/85">
              <div className="flex justify-between border-b border-viola/10 dark:border-cream/10 pb-2"><dt className="text-viola/60 dark:text-cream/60">Durata sessione</dt><dd className="font-medium">60 minuti</dd></div>
              <div className="flex justify-between border-b border-viola/10 dark:border-cream/10 pb-2"><dt className="text-viola/60 dark:text-cream/60">Cadenza</dt><dd className="font-medium">Flessibile</dd></div>
              <div className="flex justify-between border-b border-viola/10 dark:border-cream/10 pb-2"><dt className="text-viola/60 dark:text-cream/60">Setting</dt><dd className="font-medium">Online</dd></div>
              <div className="flex justify-between pb-2"><dt className="text-viola/60 dark:text-cream/60">Tariffa</dt><dd className="font-medium">Da concordare</dd></div>
            </dl>
            <Link to="/contatti" className="mt-8 btn-primary w-full justify-center" data-testid="sup-cta-ind">Richiedi videocall <ArrowRight size={16} /></Link>
          </div>

          <div className="rounded-2xl bg-viola text-cream p-8" data-testid="sup-modality-group">
            <p className="text-xs uppercase tracking-widest text-giallo font-semibold">Di gruppo</p>
            <h3 className="mt-2 font-display text-3xl">Gruppi di supervisione</h3>
            <p className="mt-6 text-cream/80 leading-relaxed">
              Percorsi articolati per livelli, in piccolo gruppo, con alternanza tra teoria, casi clinici e discussione collegiale.
            </p>
            <div className="mt-8 rounded-xl bg-cream/10 border border-cream/15 p-5">
              <p className="text-xs uppercase tracking-widest text-giallo font-semibold flex items-center gap-2"><Sparkles size={12} /> Prossimi cicli</p>
              <p className="mt-3 text-lg font-display">Info in arrivo!</p>
              <p className="mt-2 text-sm text-cream/70">Iscriviti alla newsletter per essere informatə non appena aprono le iscrizioni ai prossimi gruppi.</p>
            </div>
            <Link to="/contatti" className="mt-8 btn-secondary w-full justify-center" data-testid="sup-cta-group">Iscriviti agli aggiornamenti</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
